/**
 * Display helpers shared by the staff provider drawer and its tabs.
 *
 * Every enum-ish value is labelled through the CodeDefinition maps loaded once
 * in `ProviderDrawer` (`PROVIDER_DRAWER_CATEGORIES`) and passed down as
 * `ProviderDrawerMaps`; nothing here hardcodes a label.
 */
import { formatApiDate } from '../../../utils/date';

export const PROVIDER_DRAWER_CATEGORIES: string[] = [
  'Degree',
  'Specialty',
  'Gender',
  'Language',
  'MilitaryStatus',
  'MilitaryBranch',
  'LicenseType',
  'LicenseStatus',
  'LicenseStateChoices',
  'BoardCertificationType',
  'CertificationType',
  'ImmunizationType',
  'ProviderFileType',
  'WorkType',
  'TravelType',
  'ScheduleType',
  'ProfileDisposition',
  'RegistrationStatus',
  'SourceType',
];

/** category → code → title, as returned by `useCodeDefinitionsAsMaps`. */
export type ProviderDrawerMaps = Record<string, Record<string, string>>;

export const EMPTY_VALUE = '--';

/** Title for one code, falling back to the raw code, `--` when unset. */
export const labelFor = (
  code: string | null | undefined,
  map: Record<string, string> | undefined
): string => (code ? map?.[code] || code : EMPTY_VALUE);

/**
 * Title without its own code when the CodeDefinition title is shaped
 * "CODE (Long name)" (board / life-support certification types) — the card
 * title already shows the code, so only the long name is repeated.
 */
export const labelWithoutCode = (
  code: string | null | undefined,
  map: Record<string, string> | undefined
): string => {
  const title = labelFor(code, map);
  if (!code || title === EMPTY_VALUE) return title;
  const prefix = `${code} (`;
  return title.startsWith(prefix) && title.endsWith(')') ? title.slice(prefix.length, -1) : title;
};

/** Titles for a list of codes joined with commas, `--` when empty. */
export const labelsFor = (
  codes: readonly string[] | null | undefined,
  map: Record<string, string> | undefined
): string => (codes || []).map((code) => map?.[code] || code).join(', ') || EMPTY_VALUE;

/** API date-only strings (`YYYY-MM-DD`) rendered without a timezone shift. */
export const formatProviderDate = (value: string | null | undefined): string =>
  formatApiDate(value, { emptyLabel: EMPTY_VALUE });

export const formatHourlyRate = (rate: number | null | undefined): string =>
  rate === null || rate === undefined ? EMPTY_VALUE : `$${rate}/hr`;

export const yesNo = (value: boolean | null | undefined): string => (value ? 'Yes' : 'No');

/** Text or `--`; keeps `0` since it is a real value. */
export const textOrDash = (value: string | number | null | undefined): string =>
  value === null || value === undefined || value === '' ? EMPTY_VALUE : String(value);

/** `file_counts` is keyed by `ProviderFileType` code; missing keys mean zero uploads. */
export const fileCountFor = (
  fileCounts: Record<string, number> | null | undefined,
  fileType: string
): number => fileCounts?.[fileType] ?? 0;

export const formatFileCount = (count: number): string =>
  count === 0 ? 'None on file' : `${count} on file`;
